import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { getListings } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import { 
  Heart, ArrowRight, MapPin, X, Check, Star, Home, Users, Euro, Zap, 
  Building2, Target, Shield, MessageSquare, Clock, Sparkles, Crown,
  ChevronRight, Play, TrendingUp, Award, Timer, CheckCircle, Gift
} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { language } = useLanguage();
  const [listings, setListings] = useState([]);
  const [swipeCount, setSwipeCount] = useState(324);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [countdown, setCountdown] = useState({ hours: 23, minutes: 59, seconds: 59 });

  useEffect(() => {
    getListings({ limit: 6 }).then(data => setListings(data.listings || [])).catch(() => {});
  }, []);

  // Handle hash scrolling (e.g., /#hoe-werkt-het)
  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location.hash]);

  // Countdown timer for urgency
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        } else {
          // Reset countdown for demo purposes
          return { hours: 23, minutes: 59, seconds: 59 };
        }
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Animate swipe counter
  useEffect(() => {
    const interval = setInterval(() => {
      setSwipeCount(prev => prev + Math.floor(Math.random() * 2) + 1);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const isVerhuurder = user?.userType === 'verhuurder';

  const heroTestimonials = [
    { name: 'Fleur de V.', text: language === 'nl' ? 'Na 2 weken mijn eerste bezichtiging!' : 'Got my first viewing after 2 weeks!', image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100' },
    { name: 'Daan K.', text: language === 'nl' ? 'Eindelijk geen eindeloos mailen meer' : 'Finally no more endless emailing', image: 'https://images.unsplash.com/photo-1628619487925-e9b8fc4c6b08?w=100' },
    { name: 'Lisa M.', text: language === 'nl' ? 'Fijn dat het zo simpel werkt' : 'Great that it works so simply', image: 'https://images.unsplash.com/photo-1709202967828-e1a7823ccdf6?w=100' }
  ];

  // Translation helper for this page
  const t = {
    swipesToday: language === 'nl' ? 'swipes vandaag' : 'swipes today',
    findYour: language === 'nl' ? 'Vind jouw' : 'Find your',
    dreamHome: language === 'nl' ? 'droomwoning' : 'dream home',
    heroSubtitle: language === 'nl' 
      ? 'Swipe door honderden woningen. Match met verhuurders. Vind je nieuwe thuis in Nederland.' 
      : 'Swipe through hundreds of homes. Match with landlords. Find your new home in the Netherlands.',
    startSwiping: language === 'nl' ? 'Start met swipen' : 'Start swiping',
    freeSwipes: language === 'nl' ? 'Onbeperkt swipen' : 'Unlimited swipes',
    safeSecure: language === 'nl' ? '100% veilig' : '100% safe',
    directContact: language === 'nl' ? 'Direct contact' : 'Direct contact',
    activeUsers: language === 'nl' ? 'Actieve gebruikers' : 'Active users',
    propertiesOnline: language === 'nl' ? 'Woningen online' : 'Properties online',
    findWithin2Weeks: language === 'nl' ? 'Vindt binnen 2 weken' : 'Find within 2 weeks',
    appStoreRating: language === 'nl' ? 'Gebruikers rating' : 'User rating',
    trendingNow: language === 'nl' ? 'TRENDING NU' : 'TRENDING NOW',
    popularProperties: language === 'nl' ? 'Populaire Woningen' : 'Popular Properties',
    viewAll: language === 'nl' ? 'Bekijk alles' : 'View all',
    perMonth: language === 'nl' ? '/mnd' : '/mo',
    new: language === 'nl' ? 'NIEUW' : 'NEW',
    hot: 'HOT',
    fast: language === 'nl' ? 'SNEL' : 'FAST',
    howItWorks: language === 'nl' ? 'Zo werkt het' : 'How it works',
    step1Title: language === 'nl' ? 'Maak je profiel' : 'Create your profile',
    step1Desc: language === 'nl' ? 'Kies je profieltype en voorkeuren' : 'Choose your profile type and preferences',
    step2Title: language === 'nl' ? 'Swipe woningen' : 'Swipe properties',
    step2Desc: language === 'nl' ? 'Like of skip - zo simpel is het' : 'Like or skip - it\'s that simple',
    step3Title: language === 'nl' ? 'Match & verhuis' : 'Match & move',
    step3Desc: language === 'nl' ? 'Chat met verhuurders en plan bezichtigingen' : 'Chat with landlords and schedule viewings',
    whatUsersSay: language === 'nl' ? 'Wat gebruikers zeggen' : 'What users say',
    limitedOffer: language === 'nl' ? 'BEPERKTE AANBIEDING' : 'LIMITED OFFER',
    goPremium: language === 'nl' ? 'Ga Premium' : 'Go Premium',
    unlimitedSwipes: language === 'nl' ? 'Onbeperkt swipen' : 'Unlimited swipes',
    prioritySupport: language === 'nl' ? 'Prioriteit in reacties' : 'Priority responses',
    directMessages: language === 'nl' ? 'Direct berichten sturen' : 'Direct messaging',
    newListingsFirst: language === 'nl' ? 'Nieuwe woningen als eerste zien' : 'See new listings first',
    tryFree: language === 'nl' ? 'Start nu' : 'Start now',
    cancelAnytime: language === 'nl' ? 'Direct opzegbaar' : 'Cancel anytime',
    listPropertyFree: language === 'nl' ? 'Gratis woning adverteren' : 'List property for free',
    listPropertyDesc: language === 'nl' 
      ? 'Bereik duizenden huurders op zoek naar hun nieuwe thuis' 
      : 'Reach thousands of renters looking for their new home',
  };

  return (
    <div className="min-h-screen bg-[#F7F7F7]" data-testid="homepage">
      <Header />
      
      <main>
        {/* HERO - Ultra Dynamic with Phone Mockup */}
        <section className="relative min-h-[95vh] flex items-center overflow-hidden bg-[#0D2137]">
          {/* Animated background with property images */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0D2137] via-[#1F3A5F] to-[#0D2137]"></div>
            <div className="absolute top-0 left-0 w-full h-full opacity-30">
              <div className="absolute top-20 left-[10%] w-96 h-96 bg-[#2E7D32] rounded-full blur-[120px] animate-pulse"></div>
              <div className="absolute bottom-20 right-[10%] w-[500px] h-[500px] bg-[#2E7D32]/50 rounded-full blur-[150px] animate-pulse" style={{animationDelay: '1s'}}></div>
            </div>
            
            {/* Floating property images in background */}
            <div className="absolute inset-0 overflow-hidden opacity-20">
              <img src="https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800" alt="" className="absolute top-10 left-[5%] w-64 h-48 object-cover rounded-2xl rotate-[-5deg] animate-float" />
              <img src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800" alt="" className="absolute top-40 right-[8%] w-56 h-40 object-cover rounded-2xl rotate-[8deg] animate-float" style={{animationDelay: '0.5s'}} />
              <img src="https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=800" alt="" className="absolute bottom-32 left-[15%] w-48 h-36 object-cover rounded-2xl rotate-[5deg] animate-float" style={{animationDelay: '1s'}} />
            </div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 w-full">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left - Content */}
              <div className="text-center lg:text-left">
                {/* Live counter badge */}
                <div className="inline-flex items-center gap-2 bg-[#2E7D32] text-white px-4 py-2 rounded-full text-sm font-bold mb-6 animate-pulse">
                  <span className="w-2 h-2 bg-white rounded-full animate-ping"></span>
                  <span>{swipeCount.toLocaleString()} {t.swipesToday}</span>
                </div>

                {/* Main headline */}
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white leading-[0.9] mb-6">
                  <span className="text-[#2E7D32]">Swipe</span> {language === 'nl' ? 'je' : 'your'}
                  <br />
                  <span className="relative inline-block">
                    {t.dreamHome}
                    <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none">
                      <path d="M2 10C50 2 100 2 150 6C200 10 250 4 298 8" stroke="#2E7D32" strokeWidth="4" strokeLinecap="round"/>
                    </svg>
                  </span>
                </h1>

                <p className="text-xl md:text-2xl text-white/60 mb-8 max-w-lg mx-auto lg:mx-0">
                  {language === 'nl' 
                    ? <>82% vindt binnen <span className="text-white font-bold">2 weken</span> een woning. Jij kunt de volgende zijn.</>
                    : <>82% find a home within <span className="text-white font-bold">2 weeks</span>. You could be next.</>
                  }
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                  <Button 
                    onClick={() => navigate(isAuthenticated ? (isVerhuurder ? '/verhuurder/dashboard' : '/swipematch') : '/registreren')}
                    className="bg-[#2E7D32] hover:bg-[#256B2B] text-white h-16 px-10 text-xl font-black rounded-full shadow-2xl shadow-[#2E7D32]/30 group"
                    data-testid="hero-cta-btn"
                  >
                    {language === 'nl' ? 'Start nu gratis' : 'Start free now'}
                    <ArrowRight className="w-6 h-6 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <Button 
                    onClick={() => navigate(isAuthenticated && isVerhuurder ? '/verhuurder/onboarding' : '/verhuurder/onboarding')}
                    className="bg-white/10 border-2 border-white/30 text-white hover:bg-white hover:text-[#1F3A5F] h-16 px-8 text-lg font-bold rounded-full transition-all"
                    data-testid="hero-verhuurder-btn"
                  >
                    <Building2 className="w-5 h-5 mr-2" />
                    {t.listPropertyFree}
                  </Button>
                </div>

                {/* Trust badges */}
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-white/50 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#2E7D32]" />
                    <span>{t.freeSwipes}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#2E7D32]" />
                    <span>{t.safeSecure}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-[#2E7D32]" />
                    <span>{t.directContact}</span>
                  </div>
                </div>
              </div>

              {/* Right - Phone Mockup with Real Swipe Interface */}
              <div className="relative flex justify-center lg:justify-end">
                {/* Main phone mockup */}
                <div className="relative">
                  <div className="bg-gradient-to-b from-[#1a1a2e] to-[#16213e] rounded-[3rem] p-3 shadow-2xl w-[280px] md:w-[320px]">
                    <div className="bg-white rounded-[2.5rem] overflow-hidden aspect-[9/18]">
                      {/* Status bar */}
                      <div className="bg-[#1F3A5F] px-6 py-3 flex items-center justify-between">
                        <span className="text-white/60 text-xs">9:41</span>
                        <div className="flex items-center gap-1">
                          <div className="w-4 h-4 bg-[#2E7D32] rounded-full flex items-center justify-center">
                            <span className="text-white text-[8px] font-bold">3</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Property card */}
                      <div className="relative">
                        <img 
                          src="https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=600" 
                          alt={language === 'nl' ? 'Woning' : 'Property'} 
                          className="w-full h-52 object-cover"
                        />
                        <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                          <span className="text-xs font-bold text-[#2E7D32]">{language === 'nl' ? 'NIEUW' : 'NEW'}</span>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                          <div className="flex items-end justify-between">
                            <div>
                              <p className="text-white font-black text-2xl">€875</p>
                              <p className="text-white/80 text-xs">{language === 'nl' ? 'per week' : 'per week'}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-white font-bold text-sm">Amsterdam</p>
                              <p className="text-white/70 text-xs">Centrum • Studio</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Property details */}
                      <div className="p-4">
                        <div className="flex items-center gap-4 mb-4 text-sm text-[#9E9E9E]">
                          <span className="flex items-center gap-1"><Building2 className="w-4 h-4" /> 35m²</span>
                          <span className="flex items-center gap-1"><Users className="w-4 h-4" /> 1 {language === 'nl' ? 'pers' : 'pers'}</span>
                          <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-[#2E7D32]" /> {language === 'nl' ? 'Geverifieerd' : 'Verified'}</span>
                        </div>
                        
                        {/* Swipe buttons */}
                        <div className="flex justify-center gap-6">
                          <button className="w-16 h-16 bg-white border-2 border-[#C04A4A] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                            <X className="w-8 h-8 text-[#C04A4A]" />
                          </button>
                          <button className="w-16 h-16 bg-[#2E7D32] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                            <Heart className="w-8 h-8 text-white" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Floating elements - Match notification properly aligned */}
                  <div className="absolute -top-2 -right-2 bg-white rounded-2xl shadow-xl p-2.5 animate-bounce z-10">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-[#2E7D32] rounded-full flex items-center justify-center">
                        <Heart className="w-3.5 h-3.5 text-white fill-white" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-[#2A2A2A]">Match!</p>
                        <p className="text-[9px] text-[#9E9E9E]">{language === 'nl' ? 'Nu chatten' : 'Chat now'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="absolute -bottom-4 -left-4 bg-[#2E7D32] text-white rounded-2xl shadow-xl p-3">
                    <div className="flex items-center gap-2">
                      <Timer className="w-5 h-5" />
                      <div>
                        <p className="text-xs font-bold">{language === 'nl' ? 'Gem. 2 weken' : 'Avg. 2 weeks'}</p>
                        <p className="text-[10px] text-white/70">{language === 'nl' ? 'tot je nieuwe woning' : 'to your new home'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/30 animate-bounce">
            <ChevronRight className="w-8 h-8 rotate-90" />
          </div>
        </section>

        {/* SOCIAL PROOF BAR - Stats that impress */}
        <section className="py-8 bg-white border-b border-[#E5E5E5]">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {[
                { value: '1.247', label: t.activeUsers, icon: Users },
                { value: '320+', label: t.propertiesOnline, icon: Building2 },
                { value: '82%', label: t.findWithin2Weeks, icon: TrendingUp },
                { value: '4.8', label: t.appStoreRating, icon: Star }
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <stat.icon className="w-6 h-6 md:w-8 md:h-8 text-[#2E7D32] mx-auto mb-2" />
                  <div className="text-3xl md:text-5xl font-black text-[#2A2A2A]">{stat.value}</div>
                  <div className="text-[#9E9E9E] text-xs md:text-sm mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FREE BANNER - Replacing Premium Upsell */}
        <section className="py-4 bg-gradient-to-r from-[#2E7D32] via-[#3d9140] to-[#2E7D32] relative overflow-hidden">
          {/* Animated background elements */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-1/4 w-32 h-32 bg-white rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 right-1/4 w-40 h-40 bg-white rounded-full blur-3xl animate-pulse" style={{animationDelay: '0.5s'}}></div>
          </div>
          
          <div className="max-w-6xl mx-auto px-4 relative z-10">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4">
              {/* Center - Free announcement */}
              <div className="flex items-center gap-4 text-white">
                <div className="hidden md:flex w-12 h-12 bg-white/20 rounded-full items-center justify-center">
                  <Gift className="w-6 h-6 text-white" />
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-1 flex-wrap">
                    <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      ✨ {language === 'nl' ? '100% GRATIS' : '100% FREE'}
                    </span>
                  </div>
                  <p className="text-lg md:text-xl font-black">
                    {language === 'nl' ? 'Alle functies zijn nu gratis te gebruiken!' : 'All features are now free to use!'}
                  </p>
                  <p className="text-white/80 text-sm hidden md:block">
                    {language === 'nl' ? 'Onbeperkt swipen • Direct berichten • Geen verborgen kosten' : 'Unlimited swipes • Direct messages • No hidden costs'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PROPERTY SHOWCASE - Hot Properties */}
        <section className="py-16 md:py-24 px-4 bg-[#F7F7F7]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#C04A4A] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
                <Sparkles className="w-4 h-4" />
                {t.trendingNow}
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-[#2A2A2A] mb-4">
                {t.popularProperties}
              </h2>
              <p className="text-lg text-[#9E9E9E] max-w-xl mx-auto">
                {language === 'nl' ? 'Deze woningen worden het meest bekeken. Wees er snel bij!' : 'These properties are most viewed. Be quick!'}
              </p>
            </div>
            
            {/* Property Cards Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-10">
              {[
                { img: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=600', price: '€875', city: 'Amsterdam', area: 'Centrum', type: language === 'nl' ? 'Studio' : 'Studio', m2: '45m²', badge: language === 'nl' ? 'NIEUW' : 'NEW' },
                { img: 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=600', price: '€695', city: 'Rotterdam', area: 'Noord', type: language === 'nl' ? 'Kamer' : 'Room', m2: '22m²', badge: 'HOT' },
                { img: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600', price: '€1.250', city: 'Utrecht', area: 'Oost', type: language === 'nl' ? 'Appartement' : 'Apartment', m2: '65m²', badge: null },
                { img: 'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=600', price: '€950', city: 'Den Haag', area: 'Centrum', type: 'Studio', m2: '38m²', badge: language === 'nl' ? 'SNEL' : 'FAST' }
              ].map((item, i) => (
                <div 
                  key={i} 
                  className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all hover:-translate-y-1" 
                  onClick={() => navigate('/swipematch')}
                >
                  <div className="relative">
                    <img 
                      src={item.img} 
                      alt={item.type} 
                      className="w-full h-40 md:h-52 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {item.badge && (
                      <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold ${
                        item.badge === 'HOT' ? 'bg-[#C04A4A] text-white' : 
                        item.badge === 'SNEL' || item.badge === 'FAST' ? 'bg-[#FF9800] text-white' :
                        'bg-[#2E7D32] text-white'
                      }`}>
                        {item.badge}
                      </div>
                    )}
                    <div className="absolute bottom-3 right-3 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-lg">
                      <Heart className="w-5 h-5 text-[#2E7D32]" />
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xl md:text-2xl font-black text-[#2E7D32]">{item.price}<span className="text-sm text-[#9E9E9E] font-normal">/{language === 'nl' ? 'mnd' : 'mo'}</span></p>
                      <span className="text-xs text-[#9E9E9E] bg-[#F7F7F7] px-2 py-1 rounded-full">{item.m2}</span>
                    </div>
                    <p className="font-bold text-[#2A2A2A] text-sm">{item.city}</p>
                    <p className="text-xs text-[#9E9E9E]">{item.area} • {item.type}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center">
              <Button 
                onClick={() => navigate('/swipematch')}
                className="bg-[#2E7D32] hover:bg-[#256B2B] text-white h-14 px-8 text-lg font-bold rounded-full"
              >
                {language === 'nl' ? 'Bekijk alle 320+ woningen' : 'View all 320+ properties'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS - Visual Steps with Photos */}
        <section id="hoe-werkt-het" className="py-20 md:py-32 px-4 bg-white scroll-mt-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-[#2E7D32]/10 text-[#2E7D32] px-4 py-2 rounded-full text-sm font-bold mb-6">
                <Play className="w-4 h-4" />
                {language === 'nl' ? 'ZO WERKT HET' : 'HOW IT WORKS'}
              </div>
              <h2 className="text-4xl md:text-6xl font-black text-[#2A2A2A] mb-4">
                {language === 'nl' ? 'Van aanmelden tot' : 'From sign up to'}<br/><span className="text-[#2E7D32]">{language === 'nl' ? 'sleuteloverdracht' : 'key handover'}</span>
              </h2>
              <p className="text-xl text-[#9E9E9E] max-w-2xl mx-auto">
                {language === 'nl' ? 'In 3 simpele stappen naar je nieuwe woning. Geen gedoe, geen stress.' : 'Find your new home in 3 simple steps. No hassle, no stress.'}
              </p>
            </div>

            {/* Step 1 */}
            <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center mb-20 md:mb-32">
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#2E7D32] rounded-2xl flex items-center justify-center text-white text-2xl font-black">
                    1
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#2E7D32] uppercase tracking-wide">{language === 'nl' ? 'Stap 1' : 'Step 1'}</p>
                    <h3 className="text-3xl md:text-4xl font-black text-[#2A2A2A]">{language === 'nl' ? 'Maak je profiel' : 'Create your profile'}</h3>
                  </div>
                </div>
                <p className="text-lg text-[#9E9E9E] mb-6 leading-relaxed">
                  {language === 'nl' 
                    ? <>In slechts <span className="text-[#2A2A2A] font-bold">30 seconden</span> vertel je ons wie je bent. Student, starter, expat of professional? Selecteer je steden en budget. Klaar!</>
                    : <>In just <span className="text-[#2A2A2A] font-bold">30 seconds</span> you tell us who you are. Student, starter, expat or professional? Select your cities and budget. Done!</>
                  }
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {[
                    { icon: '🎓', label: 'Student' },
                    { icon: '💼', label: 'Starter' },
                    { icon: '🌍', label: 'Expat' },
                    { icon: '👔', label: 'Professional' }
                  ].map((type, i) => (
                    <div key={i} className={`p-4 rounded-xl border-2 ${i === 0 ? 'border-[#2E7D32] bg-[#2E7D32]/5' : 'border-[#E5E5E5]'} flex items-center gap-3`}>
                      <span className="text-2xl">{type.icon}</span>
                      <span className="font-bold text-[#2A2A2A]">{type.label}</span>
                      {i === 0 && <Check className="w-5 h-5 text-[#2E7D32] ml-auto" />}
                    </div>
                  ))}
                </div>

                <div className="bg-[#F7F7F7] rounded-xl p-4 flex items-center gap-3">
                  <Timer className="w-6 h-6 text-[#2E7D32]" />
                  <span className="text-[#9E9E9E]">{language === 'nl' ? 'Duurt gemiddeld' : 'Takes on average'} <span className="text-[#2A2A2A] font-bold">30 {language === 'nl' ? 'seconden' : 'seconds'}</span></span>
                </div>
              </div>

              <div className="order-1 lg:order-2 flex justify-center">
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600" 
                    alt={language === 'nl' ? 'Profiel aanmaken' : 'Create profile'} 
                    className="w-full max-w-md rounded-3xl shadow-2xl"
                  />
                  <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#2E7D32]/10 rounded-xl flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-[#2E7D32]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#2A2A2A]">{language === 'nl' ? 'Profiel compleet!' : 'Profile complete!'}</p>
                        <p className="text-sm text-[#9E9E9E]">{language === 'nl' ? 'Klaar om te swipen' : 'Ready to swipe'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center mb-20 md:mb-32">
              <div className="flex justify-center">
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600" 
                    alt="Swipe woningen" 
                    className="w-full max-w-md rounded-3xl shadow-2xl"
                  />
                  {/* Swipe overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex gap-8">
                      <div className="w-20 h-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-xl border-4 border-[#C04A4A]">
                        <X className="w-10 h-10 text-[#C04A4A]" />
                      </div>
                      <div className="w-20 h-20 bg-[#2E7D32] rounded-full flex items-center justify-center shadow-xl animate-pulse">
                        <Heart className="w-10 h-10 text-white fill-white" />
                      </div>
                    </div>
                  </div>
                  <div className="absolute -top-6 -left-6 bg-[#2E7D32] text-white rounded-2xl shadow-xl p-4">
                    <p className="font-black text-2xl">€750</p>
                    <p className="text-white/70 text-sm">/{language === 'nl' ? 'maand' : 'month'}</p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#2E7D32] rounded-2xl flex items-center justify-center text-white text-2xl font-black">
                    2
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#2E7D32] uppercase tracking-wide">{language === 'nl' ? 'Stap 2' : 'Step 2'}</p>
                    <h3 className="text-3xl md:text-4xl font-black text-[#2A2A2A]">Swipe & Match</h3>
                  </div>
                </div>
                <p className="text-lg text-[#9E9E9E] mb-6 leading-relaxed">
                  {language === 'nl' 
                    ? <>Net als een datingsite! Bekijk woningen die bij jou passen. <span className="text-[#2A2A2A] font-bold">Swipe rechts</span> als je interesse hebt, <span className="text-[#2A2A2A] font-bold">links</span> als het niets voor je is.</>
                    : <>Just like a dating site! View properties that match you. <span className="text-[#2A2A2A] font-bold">Swipe right</span> if interested, <span className="text-[#2A2A2A] font-bold">left</span> if not.</>
                  }
                </p>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-[#C04A4A]/10 rounded-xl p-5 text-center">
                    <X className="w-10 h-10 text-[#C04A4A] mx-auto mb-2" />
                    <p className="font-bold text-[#2A2A2A]">{language === 'nl' ? 'Swipe links' : 'Swipe left'}</p>
                    <p className="text-sm text-[#9E9E9E]">{language === 'nl' ? 'Niet interessant' : 'Not interested'}</p>
                  </div>
                  <div className="bg-[#2E7D32]/10 rounded-xl p-5 text-center">
                    <Heart className="w-10 h-10 text-[#2E7D32] mx-auto mb-2" />
                    <p className="font-bold text-[#2A2A2A]">{language === 'nl' ? 'Swipe rechts' : 'Swipe right'}</p>
                    <p className="text-sm text-[#9E9E9E]">{language === 'nl' ? 'Ik wil dit!' : 'I want this!'}</p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-[#2E7D32] to-[#1F3A5F] rounded-xl p-4 text-white">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-6 h-6" />
                    <p className="font-bold">{language === 'nl' ? '320+ woningen beschikbaar om te swipen' : '320+ properties available to swipe'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#2E7D32] rounded-2xl flex items-center justify-center text-white text-2xl font-black">
                    3
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#2E7D32] uppercase tracking-wide">{language === 'nl' ? 'Stap 3' : 'Step 3'}</p>
                    <h3 className="text-3xl md:text-4xl font-black text-[#2A2A2A]">{language === 'nl' ? 'Chat & Verhuis' : 'Chat & Move'}</h3>
                  </div>
                </div>
                <p className="text-lg text-[#9E9E9E] mb-6 leading-relaxed">
                  {language === 'nl' 
                    ? <>Bij elke swipe naar rechts wordt de woning opgeslagen. <span className="text-[#2A2A2A] font-bold">Gratis</span> chat je direct met de verhuurder. Plan een bezichtiging en teken het contract!</>
                    : <>With every swipe right, the property is saved. Chat <span className="text-[#2A2A2A] font-bold">for free</span> directly with the landlord. Schedule a viewing and sign the contract!</>
                  }
                </p>

                <div className="bg-[#F7F7F7] rounded-xl p-6 mb-6">
                  <div className="flex items-start gap-3 mb-4">
                    <MessageSquare className="w-6 h-6 text-[#2E7D32] mt-1" />
                    <div className="flex-1">
                      <p className="font-bold text-[#2A2A2A] mb-2">{language === 'nl' ? 'Direct contact:' : 'Direct contact:'}</p>
                      <div className="bg-white rounded-lg p-4 border border-[#E5E5E5]">
                        <p className="text-[#9E9E9E] italic text-sm">
                          {language === 'nl' 
                            ? '"Hoi! Ik ben geïnteresseerd in deze woning. Ik heb een vast inkomen en kan per direct verhuizen. Graag kom ik bezichtigen!"'
                            : '"Hi! I\'m interested in this property. I have a steady income and can move in immediately. I\'d love to schedule a viewing!"'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[#2E7D32]">
                    <Zap className="w-5 h-5" />
                    <span className="font-bold text-sm">{language === 'nl' ? 'Schrijf je eigen bericht aan de verhuurder' : 'Write your own message to the landlord'}</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-[#1F3A5F] to-[#0D2137] rounded-2xl p-6 text-white">
                  <p className="text-sm text-white/60 mb-4 uppercase tracking-wide font-bold">{language === 'nl' ? 'Onze resultaten' : 'Our results'}</p>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-3xl md:text-4xl font-black text-[#2E7D32]">82%</p>
                      <p className="text-xs text-white/60">Match rate</p>
                    </div>
                    <div>
                      <p className="text-3xl md:text-4xl font-black text-[#2E7D32]">&lt;2</p>
                      <p className="text-xs text-white/60">{language === 'nl' ? 'Weken gem.' : 'Weeks avg.'}</p>
                    </div>
                    <div>
                      <p className="text-3xl md:text-4xl font-black text-[#2E7D32]">320+</p>
                      <p className="text-xs text-white/60">{language === 'nl' ? 'Woningen' : 'Properties'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="order-1 lg:order-2 flex justify-center">
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600" 
                    alt={language === 'nl' ? 'Sleuteloverdracht' : 'Key handover'} 
                    className="w-full max-w-md rounded-3xl shadow-2xl"
                  />
                  <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-5">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 bg-[#2E7D32] rounded-xl flex items-center justify-center">
                        <Award className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <p className="font-black text-[#2A2A2A] text-lg">{language === 'nl' ? 'Gefeliciteerd!' : 'Congratulations!'}</p>
                        <p className="text-sm text-[#9E9E9E]">{language === 'nl' ? 'Welkom in je nieuwe thuis' : 'Welcome to your new home'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="absolute -top-4 -right-4 bg-[#2E7D32] text-white rounded-full px-4 py-2 font-bold shadow-lg animate-bounce">
                    🎉 Match!
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FREE FOR ALL - Replacing Subscription Section */}
        <section className="py-24 px-4 bg-gradient-to-br from-[#1F3A5F] to-[#0D2137] relative overflow-hidden">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#2E7D32] rounded-full blur-[150px]"></div>
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#2E7D32]/50 rounded-full blur-[120px]"></div>
          </div>

          <div className="relative max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#2E7D32] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
              <Gift className="w-4 h-4" />
              {language === 'nl' ? '100% GRATIS' : '100% FREE'}
            </div>
            
            <h2 className="text-4xl md:text-6xl font-black text-white mb-4">
              {language === 'nl' ? <>Alles gratis.<br/></> : <>Everything free.<br/></>}
              <span className="text-[#2E7D32]">{language === 'nl' ? 'Geen kosten.' : 'No costs.'}</span>
            </h2>
            <p className="text-xl text-white/70 mb-12 max-w-2xl mx-auto">
              {language === 'nl' 
                ? <>Wij geloven dat iedereen toegang moet hebben tot een goede woning. Daarom is <span className="text-white font-bold">alles gratis</span>.</>
                : <>We believe everyone deserves access to good housing. That's why <span className="text-white font-bold">everything is free</span>.</>
              }
            </p>

            <div className="bg-white rounded-3xl p-8 md:p-10 max-w-md mx-auto shadow-2xl">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Gift className="w-8 h-8 text-[#2E7D32]" />
                <span className="text-2xl font-black text-[#2A2A2A]">{language === 'nl' ? 'Gratis' : 'Free'}</span>
              </div>
              
              <div className="mb-6">
                <div className="flex items-baseline justify-center gap-2">
                  <div className="text-5xl font-black text-[#2E7D32]">€0</div>
                </div>
                <div className="text-[#9E9E9E]">{language === 'nl' ? 'voor altijd • geen verborgen kosten' : 'forever • no hidden costs'}</div>
              </div>

              <ul className="space-y-4 mb-8 text-left">
                {[
                  { text: language === 'nl' ? 'Onbeperkt swipen' : 'Unlimited swiping', emoji: '♾️' },
                  { text: language === 'nl' ? 'Direct contact met verhuurders' : 'Direct contact with landlords', emoji: '⚡' },
                  { text: language === 'nl' ? 'Automatische berichten bij match' : 'Auto messages on match', emoji: '💌' },
                  { text: language === 'nl' ? 'Alle zoekfilters' : 'All search filters', emoji: '🔍' },
                  { text: language === 'nl' ? 'Geverifieerde woningen' : 'Verified properties', emoji: '✅' }
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="text-xl">{item.emoji}</span>
                    <span className="text-[#2A2A2A] font-medium">{item.text}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => navigate(isAuthenticated ? '/swipematch/swipe' : '/registreren')}
                className="w-full bg-[#2E7D32] hover:bg-[#256B2B] text-white h-14 rounded-xl text-lg font-bold"
              >
                <Heart className="w-5 h-5 mr-2" />
                {isAuthenticated 
                  ? (language === 'nl' ? 'Start met swipen' : 'Start swiping')
                  : (language === 'nl' ? 'Gratis aanmelden' : 'Sign up free')
                }
              </Button>
              
              <p className="text-xs text-[#9E9E9E] mt-4">
                {language === 'nl' ? 'Geen creditcard nodig • Direct beginnen' : 'No credit card needed • Start immediately'}
              </p>
            </div>

            <div className="mt-8 text-white/60 text-sm">
              {language === 'nl' 
                ? <>Al <span className="text-white font-bold">1.200+</span> mensen vonden hun woning via Swipe je woning</>
                : <><span className="text-white font-bold">1,200+</span> people already found their home via Swipe je woning</>
              }
            </div>
          </div>
        </section>

        {/* VERHUURDERS SECTION */}
        <section className="py-24 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left content */}
              <div>
                <div className="inline-flex items-center gap-2 bg-[#1F3A5F]/10 text-[#1F3A5F] px-4 py-2 rounded-full text-sm font-bold mb-6">
                  <Building2 className="w-4 h-4" />
                  {language === 'nl' ? 'VOOR VERHUURDERS' : 'FOR LANDLORDS'}
                </div>
                
                <h2 className="text-4xl md:text-5xl font-black text-[#2A2A2A] leading-tight mb-6">
                  {language === 'nl' 
                    ? <><span className="text-[#2E7D32]">Gratis</span> woning plaatsen.<br />Perfecte huurder vinden.</>
                    : <><span className="text-[#2E7D32]">Free</span> property listing.<br />Find the perfect tenant.</>
                  }
                </h2>
                
                <p className="text-xl text-[#9E9E9E] mb-8">
                  {language === 'nl' 
                    ? <>Bereik 1.200+ geverifieerde huurders. Jij bepaalt wie je wilt. <span className="text-[#2A2A2A] font-medium">Studenten? Expats? Stellen? Jij kiest.</span></>
                    : <>Reach 1,200+ verified renters. You decide who you want. <span className="text-[#2A2A2A] font-medium">Students? Expats? Couples? You choose.</span></>
                  }
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  {[
                    { icon: Euro, value: '€0', label: language === 'nl' ? 'Plaatsen is gratis' : 'Listing is free' },
                    { icon: Target, value: '12.5', label: language === 'nl' ? 'Gem. reacties per woning' : 'Avg. responses per property' },
                    { icon: Clock, value: language === 'nl' ? '<2 weken' : '<2 weeks', label: language === 'nl' ? 'Gem. tijd tot huurder' : 'Avg. time to tenant' },
                    { icon: Shield, value: '100%', label: language === 'nl' ? 'Geverifieerde profielen' : 'Verified profiles' }
                  ].map((item, i) => (
                    <div key={i} className="bg-[#F7F7F7] rounded-xl p-4">
                      <item.icon className="w-6 h-6 text-[#2E7D32] mb-2" />
                      <div className="text-2xl font-black text-[#2A2A2A]">{item.value}</div>
                      <div className="text-sm text-[#9E9E9E]">{item.label}</div>
                    </div>
                  ))}
                </div>

                <Button 
                  onClick={() => navigate('/verhuurder/onboarding')}
                  className="bg-[#1F3A5F] hover:bg-[#0D2137] text-white h-14 px-8 text-lg font-bold rounded-full"
                >
                  <Home className="w-5 h-5 mr-2" />
                  {language === 'nl' ? 'Gratis woning plaatsen' : 'List property for free'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>

              {/* Right - Visual with testimonial */}
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800" 
                  alt={language === 'nl' ? 'Verhuurder' : 'Landlord'} 
                  className="w-full rounded-3xl shadow-2xl"
                />
                
                {/* Overlay card */}
                <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl p-6 shadow-xl max-w-sm">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-[#2E7D32] fill-[#2E7D32]" />
                    ))}
                  </div>
                  
                  <blockquote className="text-lg font-bold text-[#2A2A2A] mb-4">
                    {language === 'nl' 
                      ? '"Fijn om gericht huurders te kunnen vinden. Bespaart veel tijd vergeleken met andere platforms."'
                      : '"Great to be able to find tenants in a targeted way. Saves a lot of time compared to other platforms."'
                    }
                  </blockquote>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#2E7D32] to-[#1F3A5F] rounded-full flex items-center justify-center text-white font-bold">
                      J
                    </div>
                    <div>
                      <div className="font-bold text-[#2A2A2A]">Jasper K.</div>
                      <div className="text-[#9E9E9E] text-sm">{language === 'nl' ? 'Verhuurder, Amsterdam' : 'Landlord, Amsterdam'}</div>
                    </div>
                  </div>
                </div>

                {/* Stats badge */}
                <div className="absolute -top-4 -right-4 bg-[#2E7D32] text-white rounded-2xl p-4 shadow-lg">
                  <div className="text-center">
                    <p className="text-2xl font-black">82%</p>
                    <p className="text-xs text-white/70">Match rate</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS - Auto-rotating Social Proof */}
        <section className="py-20 px-4 bg-[#F7F7F7]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#2E7D32]/10 text-[#2E7D32] px-4 py-2 rounded-full text-sm font-bold mb-6">
                <Star className="w-4 h-4 fill-[#2E7D32]" />
                {language === 'nl' ? '1.200+ TEVREDEN GEBRUIKERS' : '1,200+ HAPPY USERS'}
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-[#2A2A2A]">
                {language === 'nl' ? 'Zij vonden hun woning via' : 'They found their home via'}<br/><span className="text-[#2E7D32]">Swipe je woning</span>
              </h2>
            </div>

            {/* Featured testimonial - Auto rotating */}
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl mb-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#2E7D32]/5 rounded-full blur-3xl"></div>
              <div className="relative">
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <img 
                    src={[
                      'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200',
                      'https://images.unsplash.com/photo-1628619487925-e9b8fc4c6b08?w=200',
                      'https://images.unsplash.com/photo-1709202967828-e1a7823ccdf6?w=200'
                    ][activeTestimonial]}
                    alt="Testimonial"
                    className="w-24 h-24 rounded-full object-cover border-4 border-[#2E7D32]"
                  />
                  <div className="flex-1 text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                      ))}
                    </div>
                    <p className="text-2xl md:text-3xl font-bold text-[#2A2A2A] mb-4">
                      &ldquo;{language === 'nl' ? [
                        'Na 2 weken zoeken via het platform kreeg ik mijn eerste bezichtiging. Veel makkelijker dan zelf alles mailen!',
                        'Als starter op de woningmarkt is dit echt een uitkomst. Geen gedoe meer met standaard reacties.',
                        'Fijn platform om snel te zien wat er beschikbaar is. De swipe functie maakt het echt makkelijk.'
                      ][activeTestimonial] : [
                        'After 2 weeks of searching via the platform I got my first viewing. Much easier than emailing everything myself!',
                        'As a starter on the housing market, this is really a lifesaver. No more hassle with standard responses.',
                        'Great platform to quickly see what\'s available. The swipe feature makes it really easy.'
                      ][activeTestimonial]}&rdquo;
                    </p>
                    <p className="text-lg text-[#9E9E9E]">
                      <span className="text-[#2A2A2A] font-bold">
                        {['Fleur de V.', 'Daan K.', 'Lisa M.'][activeTestimonial]}
                      </span> • {language === 'nl' 
                        ? ['Student, Amsterdam', 'Starter, Rotterdam', 'Young professional, Utrecht'][activeTestimonial]
                        : ['Student, Amsterdam', 'Starter, Rotterdam', 'Young professional, Utrecht'][activeTestimonial]
                      }
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Dots */}
              <div className="flex justify-center gap-2 mt-8">
                {[0, 1, 2].map((i) => (
                  <button
                    key={i}
                    onClick={() => setActiveTestimonial(i)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      i === activeTestimonial ? 'bg-[#2E7D32] w-8' : 'bg-[#E5E5E5]'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Mini testimonials */}
            <div className="grid md:grid-cols-3 gap-6">
              {(language === 'nl' ? [
                { quote: '"Fijn platform, overzichtelijk!"', name: 'Ruben T.', city: 'Den Haag', emoji: '👍' },
                { quote: '"Scheelt veel tijd met zoeken"', name: 'Anna B.', city: 'Eindhoven', emoji: '⏰' },
                { quote: '"Makkelijk in gebruik"', name: 'Sander V.', city: 'Groningen', emoji: '✨' }
              ] : [
                { quote: '"Great platform, very clear!"', name: 'Ruben T.', city: 'The Hague', emoji: '👍' },
                { quote: '"Saves a lot of time searching"', name: 'Anna B.', city: 'Eindhoven', emoji: '⏰' },
                { quote: '"Easy to use"', name: 'Sander V.', city: 'Groningen', emoji: '✨' }
              ]).map((t, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 shadow-lg border border-[#E5E5E5] hover:shadow-xl transition-shadow">
                  <div className="text-3xl mb-4">{t.emoji}</div>
                  <p className="text-lg font-bold text-[#2A2A2A] mb-3">{t.quote}</p>
                  <p className="text-[#9E9E9E] text-sm">{t.name} • {t.city}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA - Ultra Conversion */}
        <section className="relative py-24 md:py-32 px-4 overflow-hidden bg-gradient-to-br from-[#2E7D32] via-[#256B2B] to-[#1F3A5F]">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-full h-full">
              <div className="absolute -top-20 -left-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
              <div className="absolute -bottom-20 -right-20 w-[500px] h-[500px] bg-white rounded-full blur-3xl"></div>
            </div>
          </div>

          <div className="relative max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
              <Zap className="w-4 h-4" />
              {language === 'nl' ? 'JOIN 1.200+ GEBRUIKERS' : 'JOIN 1,200+ USERS'}
            </div>
            
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
              {language === 'nl' ? 'Jouw droomwoning' : 'Your dream home'}<br/>{language === 'nl' ? 'wacht op je' : 'awaits you'}
            </h2>
            
            <p className="text-xl md:text-2xl text-white/80 mb-10 max-w-2xl mx-auto">
              <span className="text-white font-bold">{language === 'nl' ? 'Onbeperkt swipen' : 'Unlimited swipes'}</span> {language === 'nl' ? '- helemaal gratis!' : '- completely free!'} 
              {language === 'nl' ? 'Geen creditcard nodig. Geen verplichtingen.' : 'No credit card needed. No obligations.'}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
              <Button 
                onClick={() => navigate(isAuthenticated ? '/swipematch' : '/registreren')}
                className="bg-white text-[#2E7D32] hover:bg-gray-100 h-16 px-12 text-xl font-black rounded-full shadow-2xl hover:shadow-none transition-all group"
              >
                {language === 'nl' ? 'START NU GRATIS' : 'START FREE NOW'}
                <ArrowRight className="w-6 h-6 ml-3 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                onClick={() => navigate('/verhuurder/onboarding')}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 h-16 px-10 text-lg font-bold rounded-full"
              >
                <Building2 className="w-5 h-5 mr-2" />
                {language === 'nl' ? 'Ik ben verhuurder' : 'I\'m a landlord'}
              </Button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-8 text-white/60 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-white" />
                <span>{language === 'nl' ? 'Gratis te proberen' : 'Free to try'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-white" />
                <span>{language === 'nl' ? '100% veilig & privé' : '100% safe & private'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-white" />
                <span>{language === 'nl' ? '1.200+ gebruikers' : '1,200+ users'}</span>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* CSS for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(2deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default HomePage;
